import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { birthYearRange, type AgeGroupPublic, type SportPublic, type TeamPublic } from "@dugads/shared";
import * as api from "@/lib/api";
import { Button, buttonVariants } from "@/components/ui/button";
import { useAuth } from "@/lib/auth-context";
import { SchoolFilter, useSchoolFilterIds } from "@/components/school-filter";
import { SchoolCrest } from "@/components/school-crest";
import { useGridPreferences } from "@/lib/use-grid-preferences";
import {
  DataTable, type DataTableColumn, type DataTableFetchParams,
} from "@/components/data-table";
import {
  ChevronDown,
  ChevronRight,
  Plus,
  Users,
  Shield,
  Eye,
} from "lucide-react";
import { InlineTeamBuilder } from "@/components/team-builder/inline-team-builder";
import { SendMessageButton } from "@/components/send-message-button";

interface SportAgeGroups {
  sport: SportPublic;
  ageGroups: AgeGroupPublic[];
}

// Flat-table columns for the unscoped (super-admin) Teams view.
const flatColumns: DataTableColumn<TeamPublic>[] = [
  { key: "name", header: "Name", sortable: true, className: "font-medium", render: (t) => t.display_name },
  {
    key: "school", header: "School", sortable: true, className: "text-muted-foreground",
    render: (t) => (
      <div className="flex items-center gap-2">
        <SchoolCrest name={t.school_name} logoUrl={t.school_logo_url} primaryColor={t.school_primary_color} size={22} />
        <span>{t.school_name}</span>
      </div>
    ),
  },
  { key: "sport", header: "Sport", className: "text-muted-foreground", render: (t) => t.sport_name },
  { key: "age_group", header: "Age Group", sortable: true, className: "text-muted-foreground", render: (t) => t.age_group },
  { key: "players", header: "Players", className: "text-muted-foreground", render: (t) => t.player_count },
  {
    key: "actions", header: "", headerClassName: "w-20", cardRole: "action",
    render: (t) => (
      <Link to={`/teams/${t.slug}`} className={buttonVariants({ variant: "ghost", size: "sm" })} data-action="view" onClick={(e) => e.stopPropagation()}>
        <Eye className="h-4 w-4" />
      </Link>
    ),
  },
];

export default function TeamsPage() {
  const { user: authUser } = useAuth();
  const canManage = authUser?.roles.some((r) => r.role_type !== "super_parent" && r.role_type !== "parent" && r.role_type !== "player") ?? false;
  const schoolIds = useSchoolFilterIds();
  const navigate = useNavigate();

  useGridPreferences("teams");

  // --- Hierarchical state (school-scoped) ---
  const [sports, setSports] = useState<SportPublic[]>([]);
  const [sportAgeGroups, setSportAgeGroups] = useState<SportAgeGroups[]>([]);
  const [selectedSportId, setSelectedSportId] = useState<string | null>(null);
  const [expandedAgeGroups, setExpandedAgeGroups] = useState<Set<string>>(new Set());

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [flatState, setFlatState] = useState<"loading" | "error" | "empty" | "ready">("loading");

  const effectiveSchoolId = schoolIds[0] ?? "";
  const isUnscoped = schoolIds.length === 0;

  // Unscoped (super-admin) flat list -> server-side search/sort/pagination.
  const fetchAllTeams = useCallback(
    async (params: DataTableFetchParams): Promise<{ data: TeamPublic[]; total: number }> => {
      const res = await api.getAllTeams({
        page: params.page,
        per_page: params.per_page,
        search: params.search,
        sort: params.sort,
        order: params.order,
      });
      return { data: res.teams, total: res.total };
    },
    [],
  );

  // Load school sports (only when school-scoped)
  useEffect(() => {
    if (isUnscoped || !effectiveSchoolId) return;
    api.getSchoolSports(effectiveSchoolId)
      .then((res) => {
        const mapped: SportPublic[] = res.sports.map((s) => ({ id: s.sport_id, name: s.sport_name, default_court_type_id: null, court_type_name: s.court_type_name ?? null, enabled: true }));
        setSports(mapped);
        const rugby = mapped.find((s) => s.name.toLowerCase() === "rugby");
        if (rugby) setSelectedSportId(rugby.id);
        else if (mapped.length > 0) setSelectedSportId(mapped[0].id);
      })
      .catch(() => {});
  }, [isUnscoped, effectiveSchoolId]);

  // Load age groups per sport when school is selected
  useEffect(() => {
    if (isUnscoped || !effectiveSchoolId || sports.length === 0) return;
    setLoading(true);
    setError(null);
    Promise.all(
      sports.map((sport) =>
        api.getAgeGroups(effectiveSchoolId, sport.id)
          .then((res) => ({ sport, ageGroups: res.age_groups }))
          .catch(() => ({ sport, ageGroups: [] as AgeGroupPublic[] })),
      ),
    )
      .then((results) => {
        const withGroups = results.filter((r) => r.ageGroups.length > 0);
        setSportAgeGroups(withGroups);
      })
      .catch((e) => setError(e instanceof api.ApiError ? e.message : "Failed to load age groups"))
      .finally(() => setLoading(false));
  }, [isUnscoped, effectiveSchoolId, sports.map((s) => s.id).join(",")]);

  const availableSports = sportAgeGroups.map((sg) => sg.sport);
  const activeSport = sportAgeGroups.find((sg) => sg.sport.id === selectedSportId);
  // 129 # 129 -- BA wants age-hierarchy order: Open first (highest sort_order
  // within), then non-Open descending. API returns ascending sort_order for
  // event-form / team-form pickers; sort client-side here so other consumers
  // aren't affected.
  const filteredAgeGroups = [...(activeSport?.ageGroups ?? [])].sort((a, b) => {
    if (a.is_open !== b.is_open) return a.is_open ? -1 : 1;
    return b.sort_order - a.sort_order;
  });

  function toggleAgeGroup(agId: string) {
    setExpandedAgeGroups((prev) => {
      const next = new Set(prev);
      if (next.has(agId)) next.delete(agId); else next.add(agId);
      return next;
    });
  }

  return (
    <div
      className="flex flex-col gap-6"
      data-page="teams-list"
      data-state={isUnscoped ? flatState : loading ? "loading" : error ? "error" : "ready"}
    >
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-semibold">Teams</h1>
          <p className="text-muted-foreground">
            Sports, age groups, and player pools
          </p>
        </div>
        {effectiveSchoolId && canManage && (
          <Link
            to={`/schools/${effectiveSchoolId}/teams/new`}
            className={buttonVariants({ size: "sm" })}
            data-action="create"
          >
            <Plus className="mr-2 h-4 w-4" />
            Add Team
          </Link>
        )}
      </div>

      {/* Filter bar */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center" data-region="filters">
        <SchoolFilter />
      </div>

      {error && <p className="text-sm text-destructive" data-error>{error}</p>}
      {loading && !isUnscoped && <p className="text-muted-foreground">Loading...</p>}

      {/* ===== UNSCOPED: flat table ===== */}
      {isUnscoped && (
        <div data-region="results">
          <DataTable
            columns={flatColumns}
            fetcher={fetchAllTeams}
            rowKey={(t) => t.id}
            rowProps={(t) => ({ "data-item": "team", "data-item-id": t.id })}
            searchPlaceholder="Search teams..."
            defaultSort="name"
            emptyMessage="No teams found"
            onRowClick={(t) => navigate(`/teams/${t.slug}`)}
            onStateChange={setFlatState}
          />
        </div>
      )}

      {/* ===== SCHOOL-SCOPED: hierarchical view ===== */}
      {!loading && !error && !isUnscoped && (
        <>
          {/* Sport facet filter */}
          {availableSports.length > 0 && (
            <div className="flex items-center gap-2 flex-wrap" data-region="filters">
              {availableSports.map((sport) => (
                <div key={sport.id} className="flex items-center gap-1">
                  <Button
                    variant={selectedSportId === sport.id ? "default" : "outline"}
                    size="sm"
                    onClick={() => setSelectedSportId(sport.id)}
                  >
                    {sport.name}
                  </Button>
                  {effectiveSchoolId && (
                    <SendMessageButton
                      contextType="sport"
                      contextId={sport.id}
                      contextLabel={sport.name}
                      schoolId={effectiveSchoolId}
                      variant="ghost"
                    />
                  )}
                </div>
              ))}
            </div>
          )}

          {sportAgeGroups.length === 0 && effectiveSchoolId && (
            <div className="flex flex-col items-center gap-2 rounded-lg border border-dashed border-border py-12">
              <p className="text-muted-foreground">No teams configured yet</p>
              <p className="text-sm text-muted-foreground">
                Create teams under age groups to see them here
              </p>
            </div>
          )}

          {filteredAgeGroups.length > 0 && (
            <div className="rounded-lg border border-border" data-region="results">
              {filteredAgeGroups.map((ag) => (
                <div key={ag.id} className="border-b border-border last:border-b-0" data-item="age-group" data-item-id={ag.id}>
                  <div className="flex items-center gap-2 px-4 py-2.5 hover:bg-muted/30">
                    <button
                      type="button"
                      onClick={() => toggleAgeGroup(ag.id)}
                      className="flex items-center gap-2 text-left flex-1 min-w-0"
                    >
                      {expandedAgeGroups.has(ag.id) ? (
                        <ChevronDown className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                      ) : (
                        <ChevronRight className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                      )}
                      <span className="font-medium">{ag.name}</span>
                      {(() => {
                        const { min, max } = birthYearRange(ag);
                        const years = min === max ? `${min}` : `${min}-${max}`;
                        return (
                          <span className="text-xs text-muted-foreground">
                            Born {years}
                          </span>
                        );
                      })()}
                    </button>
                    <div className="flex items-center gap-3 shrink-0">
                      <span className="flex items-center gap-1 text-xs text-muted-foreground" title="Teams">
                        <Shield className="h-3.5 w-3.5" />
                        {ag.team_count}
                      </span>
                      <span className="flex items-center gap-1 text-xs text-muted-foreground" title="Eligible players">
                        <Users className="h-3.5 w-3.5" />
                        {ag.player_count}
                      </span>
                      <div className="flex gap-0.5">
                        <SendMessageButton
                          contextType="age_group"
                          contextId={ag.id}
                          contextLabel={`${ag.name} ${activeSport?.sport.name ?? ""}`}
                          schoolId={effectiveSchoolId ?? undefined}
                          variant="ghost"
                        />
                        {ag._actions.includes("view") && (
                          <Link
                            to={`/age-groups/${ag.id}`}
                            className={buttonVariants({ variant: "ghost", size: "sm" })}
                            title="View player pool"
                            data-action="view"
                          >
                            <Eye className="h-3.5 w-3.5" />
                          </Link>
                        )}
                      </div>
                    </div>
                  </div>

                  {expandedAgeGroups.has(ag.id) && effectiveSchoolId && (
                    <InlineTeamBuilder ageGroup={ag} schoolId={effectiveSchoolId} />
                  )}
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}
